import React, { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

import { Patient, ImageData } from '../../utils/types'
import { get, post } from '../../utils/http'
import { LoadingAnimeSpinner } from '../common/Loaders'
import fallBackImage from '../../assets/pixel-tile.jpg'
import { Button, IconButton } from '@mui/material'
import { Replay } from '@mui/icons-material'
import { viewerS3Url } from '../../utils/config'

interface Props {
  patientId: number
  reloadViewer: Function
}

export default function PatientViewer({ patientId, reloadViewer }: Props) {
    const [patient, setPatient] = useState<Patient | null>(null)
    const [slides, setSlides] = useState<ImageData[]>([])
    const [loader, setLoader] = useState<boolean>(false)
    const [slideLoader, setSlideLoader] = useState<boolean>(false)
    const [image, setImage] = useState<ImageData | null>(null)
    
    const handleImgError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.src = fallBackImage
        e.currentTarget.onerror = null
    }

    const getPatientData = async () => {
        setLoader(true)
        try {
            const res: Patient = await get(`Patients/${patientId}`)
            if (res.dob) res.dob = res.dob.split(' ')[0]
            else res.dob = ''
            setPatient(res)
        } catch (err) {
            console.log(err)
        } finally {
            setLoader(false)
            setSlides([])
            setImage(null)
        }
    }

    const getSlideData = async () => {
        setSlideLoader(true)
        try {
            const res = await post('Images/from-list', { image_ids: patient!.slides })
            setSlides(res)
        } catch (err) {
            console.log(err)
        } finally {
            setSlideLoader(false)
        }
    }

    useEffect(() => {
        if (patient) getSlideData()   
    }, [patient])   // eslint-disable-line

    useEffect(() => {
        if (patientId > 0) getPatientData()
    }, [patientId]) // eslint-disable-line

    const selectSlide = (data: ImageData) => setImage(data)

    return (<>
    {loader
        ? <Box className="w-100 h-100" sx={{ zIndex: 999 }}>
            <LoadingAnimeSpinner />
        </Box>
        : patient
            ? <Box sx={{p: 5}} className='d-flex flex-column gap-3 flex-grow-1 position-relative pview-container'>
                <Box className='d-flex justify-content-between gap-3'>
                    <Typography className="text-limit" sx={{maxWidth: 'calc(100vw - 488px)'}} variant='h4' component='h1' title={patient.patient_id}>
                        {patient.patient_id}
                    </Typography>
                    <IconButton onClick={() => reloadViewer()}>
                        <Replay />
                    </IconButton>
                </Box>
                <Box className='d-flex flex-column gap-3'>
                    <Typography>
                        Gleason primary: {patient.gleason_primary ? patient.gleason_primary : ''}
                    </Typography>
                    <Typography>
                        Gleason secondary: {patient.gleason_secondary ? patient.gleason_secondary : ''}
                    </Typography>
                    <Typography>
                        Clinical stage: {patient.clinical_stage ? patient.clinical_stage : ''}
                    </Typography>
                </Box>
                <Box sx={{overflow: 'auto', bgcolor: '#f0f0f0', p: 3}}>
                    {slides.length
                        ? <Box className='d-flex flex-wrap gap-4 p-3 w-100'>
                            {slides.map(i => (
                                <Box
                                    key={i.id}
                                    onClick={() => selectSlide(i)}
                                    sx={{outline: image?.id === i.id ? '2px solid dodgerblue' : '1.5px solid grey', maxWidth: 200}}
                                >
                                    <img
                                        src={`${viewerS3Url}/${i.image_unique_id}/thumbnail.webp?w=512&fit=crop&auto=format`}
                                        onError={handleImgError}
                                        style={{maxWidth: 200, maxHeight: 200}}
                                        alt="Slide"
                                        loading="lazy"
                                    />
                                    <figcaption style={{textAlign: 'center'}}>
                                        <Typography className="text-limit" sx={{fontSize: '0.75rem'}} title={i.name}>{i.name}</Typography>
                                    </figcaption>
                                </Box>
                            ))}
                        </Box>
                        : slideLoader
                            ? <Box className="w-100 h-100">
                                <LoadingAnimeSpinner />
                            </Box>
                            : <Typography>No slides found</Typography>
                    }
                </Box>
                <Box className='d-flex justify-content-end'>
                    <Button component='a' disabled={!image} target='_blank' href={`/viewer/${image?.id}`}>Go to Slide</Button>
                </Box>
            </Box>
            : <Box className='pview-container' sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexGrow: 1,
                color: 'grey',
                p: 5
            }}>
                <Typography variant='h5'>Select a patient to view status</Typography>
            </Box>
    }
    </>)
}